import type { PropsWithChildren, ReactNode } from 'react'

import { minWidth, useDimensions } from '../hooks/useDimensions'

type TResponsiveDisplayProps = {
  size: keyof typeof minWidth
  behavior?: 'exact' | 'maxWidth' | 'minWidth'
}

const widths = [minWidth.xs, minWidth.sm, minWidth.xm, minWidth.md, minWidth.lg, minWidth.xl]

/**
 * ResponsiveDisplay is a component that displays its children only if the window size matches the specified size
 * and behavior.
 * Usage Example :
 *
 *  <ResponsiveDisplay size="sm" behavior="minWidth">
 *      <GradientBackground />
 *  </ResponsiveDisplay>
 *
 * @param {TResponsiveDisplayProps} props The props of the component.
 * @param {keyof typeof minWidth} props.size The size of the screen that the component should match.
 * @param {behavior} props.behavior The behavior of the component. Can be 'exact', 'maxWidth', or 'minWidth'.
 * If 'exact', the component will only be displayed if the window size is exactly equal to the specified size.
 * If 'maxWidth', the component will be displayed if the window size is less or equal to the specified size.
 * If 'minWidth', the component will be displayed if the window size is greater or equal to the specified size.
 * @param {JSX.Element | JSX.Element[]} props.children The children of the component.
 * @returns {JSX.Element | null} The component or null if the window size does not match the specified size and behavior.
 */
export const ResponsiveDisplay = ({
  children,
  behavior = 'exact',
  size,
}: PropsWithChildren<TResponsiveDisplayProps>): ReactNode => {
  const { width, height, lessThan, ...result } = useDimensions()

  const canDisplay = (() => {
    const screenSize = Object.keys(result).find((key) => result[key])

    if (behavior === 'exact') {
      return result[size]
    } else if (behavior === 'maxWidth' && screenSize) {
      return widths.indexOf(minWidth[screenSize]) <= widths.indexOf(minWidth[size])
    }
    if (screenSize) return widths.indexOf(minWidth[screenSize]) >= widths.indexOf(minWidth[size])

    return false
  })()
  return canDisplay ? children : null
}
